/* A hierarchy, shown as one.
 *
 * Fourteen in-scope DocTypes are trees. Chart of Accounts as 126 alphabetical
 * rows is the clearest example of the problem this whole product exists to
 * fix: all the data, none of the meaning. An accountant looks for *Current
 * Assets* and opens it.
 *
 * Nothing here is per-DocType: Frappe records the parent field on the DocType
 * itself, so this draws Cost Centers, Item Groups and Warehouses the same way,
 * including a hierarchy a tenant adds next year.
 *
 * It is a real ARIA tree — arrow keys move, Right opens, Left closes, Enter
 * opens the record — because a hierarchy you can only navigate with a mouse
 * is one a keyboard user cannot navigate at all.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { EmptyFirstTime, Failed, Loading } from '@jackdesk/shell'
import type { ErpError } from './api'
import { useErp } from './context'
import { formatCurrency } from './format'
import type { Doc } from './types'

type Node = {
  name: string
  isGroup: boolean
  row: Doc
  depth: number
  parent: string | null
}

type Props = {
  doctype: string
  title?: string
  onOpen: (name: string) => void
  onNew?: () => void
  /** Columns to show beside the name, if the row carries them. */
  extras?: string[]
  /** Anything the screen wants beside the New button. */
  actions?: React.ReactNode
}

export function TreeView({ doctype, title, onOpen, onNew, extras = [], actions }: Props) {
  const erp = useErp()
  const [nodes, setNodes] = useState<Node[]>([])
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [loadingUnder, setLoadingUnder] = useState<Set<string>>(new Set())
  const [error, setError] = useState<ErpError | null>(null)
  const [booting, setBooting] = useState(true)
  const [cursor, setCursor] = useState(0)
  /* Chart of Accounts carries a balance per account, rolled up the tree. A
   * chart of accounts without them is a list of account names: the figure is
   * the reason an accountant opens it, and its absence was the first thing
   * anyone noticed against the ERPNext desk. */
  const [balances, setBalances] = useState<Record<string, number | null>>({})
  const [currency, setCurrency] = useState<string | undefined>()
  const list = useRef<HTMLDivElement>(null)

  const fetchLevel = useCallback(async (parent: string | null, depth: number) => {
    const answer = await erp.treeChildren(doctype, parent ?? undefined)
    if (answer.balances) {
      // Merged rather than replaced: each level answers for its own rows.
      setBalances((held) => ({ ...held, ...answer.balances }))
      setCurrency((held) => held ?? answer.currency)
    }
    return (answer.children ?? []).map((row) => ({
      name: String(row.name),
      isGroup: Boolean(row.is_group),
      row,
      depth,
      parent,
    }))
  }, [erp, doctype])

  useEffect(() => {
    let cancelled = false
    setBooting(true); setError(null); setNodes([]); setOpen(new Set()); setBalances({})
    fetchLevel(null, 0)
      .then((roots) => { if (!cancelled) setNodes(roots) })
      .catch((e) => { if (!cancelled) setError(e as ErpError) })
      .finally(() => { if (!cancelled) setBooting(false) })
    return () => { cancelled = true }
  }, [fetchLevel])

  const toggle = async (node: Node) => {
    if (!node.isGroup) { onOpen(node.name); return }
    if (open.has(node.name)) {
      // Collapsing drops the whole subtree: keeping it would mean a stale
      // branch reappears when the parent is opened again.
      setOpen((o) => { const next = new Set(o); next.delete(node.name); return next })
      setNodes((current) => current.filter((n) => !isUnder(current, n, node.name)))
      return
    }
    setLoadingUnder((l) => new Set(l).add(node.name))
    try {
      const kids = await fetchLevel(node.name, node.depth + 1)
      setNodes((current) => {
        const at = current.findIndex((n) => n.name === node.name)
        if (at < 0) return current
        return [...current.slice(0, at + 1), ...kids, ...current.slice(at + 1)]
      })
      setOpen((o) => new Set(o).add(node.name))
    } catch (e) {
      setError(e as ErpError)
    } finally {
      setLoadingUnder((l) => { const next = new Set(l); next.delete(node.name); return next })
    }
  }

  const onKey = (e: React.KeyboardEvent) => {
    const node = nodes[cursor]
    if (!node) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((i) => Math.min(i + 1, nodes.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); if (node.isGroup && !open.has(node.name)) void toggle(node) }
    else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (node.isGroup && open.has(node.name)) void toggle(node)
      else if (node.parent) setCursor(nodes.findIndex((n) => n.name === node.parent))
    }
    else if (e.key === 'Enter') { e.preventDefault(); node.isGroup ? void toggle(node) : onOpen(node.name) }
    else if (e.key === 'Home') { e.preventDefault(); setCursor(0) }
    else if (e.key === 'End') { e.preventDefault(); setCursor(nodes.length - 1) }
  }

  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[data-cursor="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  if (booting) return <div className="page"><Loading rows={8} label={`Loading ${doctype}`} /></div>
  if (error && nodes.length === 0) {
    return <div className="page"><Failed message={error.message} requestId={error.requestId} /></div>
  }

  return (
    <div className="page" style={{ display: 'grid', gap: 14 }}>
      <div className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>{title ?? doctype}</h1>
          <div className="small muted">
            {nodes.length === 0 ? 'Nothing here yet'
              : `${nodes.length} shown · open a group to see what is inside it`}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {actions}
          {onNew ? <button className="btn btn-primary" onClick={onNew}>New</button> : null}
        </div>
      </div>

      {/* A hierarchy with nothing in it drew an empty box under a heading that
          said "0 shown · open a group to see what is inside it" — advice about
          groups that are not there, over a blank rectangle. The flat list had
          said the right thing all along; the tree had simply never been opened
          on a DocType with no rows. This is what Quality Procedures looked
          like on a site that has none. */}
      {nodes.length === 0 ? (
        <EmptyFirstTime what={(title ?? doctype).toLowerCase()}
                        action={onNew ? <button className="btn btn-primary" onClick={onNew}>Create the first one</button> : undefined} />
      ) : (
      <div className="card" style={{ overflow: 'hidden' }}>
        <div ref={list} role="tree" aria-label={title ?? doctype} tabIndex={0} onKeyDown={onKey}
             style={{ maxHeight: '72vh', overflowY: 'auto', padding: 4 }}>
          {nodes.map((node, i) => (
            <div
              key={node.name}
              role="treeitem"
              aria-level={node.depth + 1}
              aria-expanded={node.isGroup ? open.has(node.name) : undefined}
              aria-selected={i === cursor}
              data-cursor={i === cursor}
              className="navitem"
              style={{
                paddingLeft: 8 + node.depth * 20,
                background: i === cursor ? 'var(--accent-soft)' : undefined,
                color: i === cursor ? 'var(--accent)' : undefined,
              }}
              onMouseEnter={() => setCursor(i)}
              onClick={() => void toggle(node)}
            >
              <span style={{ width: 16, flex: 'none', display: 'grid', placeItems: 'center' }}>
                {node.isGroup
                  ? (loadingUnder.has(node.name)
                      ? <span className="small muted">·</span>
                      : <Caret open={open.has(node.name)} />)
                  : null}
              </span>
              <span className="grow truncate" style={{ fontWeight: node.isGroup ? 600 : 450 }}>
                {node.name}
              </span>
              {extras.map((key) => (
                node.row[key] ? (
                  <span key={key} className="small muted truncate" style={{ maxWidth: 140 }}>
                    {String(node.row[key])}
                  </span>
                ) : null
              ))}
              <Balance amount={balances[node.name]} currency={currency} />
              {node.isGroup ? null : (
                <button type="button" className="btn btn-quiet btn-sm"
                        onClick={(e) => { e.stopPropagation(); onOpen(node.name) }}>Open</button>
              )}
            </div>
          ))}
        </div>
      </div>
      )}
      {error ? <div className="small err" role="alert">{error.message}</div> : null}
    </div>
  )
}

/** One account's balance, said the way an accountant reads it.
 *
 *  ERPNext computes a balance as debit minus credit and then shows its size
 *  with Dr or Cr beside it, never a minus sign — so an income account with a
 *  credit balance of 36,400 reads "36,400 Cr" rather than "-36,400". Matching
 *  that exactly is the point: the two screens are meant to agree, and a sign
 *  convention of our own would make an accountant check every figure twice. */
function Balance({ amount, currency }: { amount?: number | null; currency?: string }) {
  if (amount === undefined || amount === null) return null
  const rounded = Math.round(amount * 100) / 100
  return (
    <span className="small muted" style={{ fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
      {formatCurrency(Math.abs(rounded), currency)} {rounded < 0 ? 'Cr' : 'Dr'}
    </span>
  )
}

/** Everything below `ancestor` in the currently drawn list. */
function isUnder(all: Node[], node: Node, ancestor: string): boolean {
  let current: Node | undefined = node
  const byName = new Map(all.map((n) => [n.name, n]))
  while (current?.parent) {
    if (current.parent === ancestor) return true
    current = byName.get(current.parent)
  }
  return false
}

const Caret = ({ open }: { open: boolean }) => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8"
       aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform var(--ms)' }}>
    <path d="M4 2.5 8 6l-4 3.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
